import { useQuery } from '@tanstack/react-query';
import { executionsAPI, automationsAPI } from '@/shared/lib/api';
import { StatsCard } from '../components/StatsCard';
import { RecentExecutions } from '../components/RecentExecutions';
import { AutomationsList } from '../components/AutomationsList';
import { ActivityChart } from '../components/ActivityChart';
import { TimeSavedTrendChart } from '../components/TimeSavedTrendChart';
import { Zap, PlayCircle, CheckCircle, Clock, Timer } from 'lucide-react';
import { formatNumber, formatPercentage, formatTimeSaved } from '@/shared/lib/utils';
import type { Automation } from '@/shared/types';

export default function Dashboard() {
  // OTIMIZADO - 1 request ao invés de 3
  const { data: dashboardData, isLoading: isDashboardLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await executionsAPI.getDashboardStats();
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  const { data: automations, isLoading: isAutomationsLoading } = useQuery({
    queryKey: ['automations-aggregated-all'],
    queryFn: async () => {
      const response = await automationsAPI.listAggregated({});
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  const isLoading = isDashboardLoading || isAutomationsLoading;

  const activeAutomations = automations?.filter((a: Automation) => a.isActive).length || 0;
  const totalAutomations = automations?.length || 0;

  const executionStats = dashboardData?.stats;

  // Loading state - mostrar skeleton apenas enquanto carrega
  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div>
          <div className="h-9 w-48 bg-muted animate-pulse rounded" />
          <div className="h-5 w-96 bg-muted animate-pulse rounded mt-1" />
        </div>

        {/* Stats Grid Skeleton */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-card rounded-lg border p-6 animate-pulse">
              <div className="h-5 w-32 bg-muted rounded mb-4" />
              <div className="h-8 w-24 bg-muted rounded mb-2" />
              <div className="h-4 w-full bg-muted rounded" />
            </div>
          ))}
        </div>

        {/* Charts Skeleton */}
        <div className="grid gap-4 md:grid-cols-2">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="bg-card rounded-lg border p-6 animate-pulse">
              <div className="h-6 w-40 bg-muted rounded mb-4" />
              <div className="h-64 bg-muted rounded" />
            </div>
          ))}
        </div>

        {/* Lists Skeleton */}
        <div className="grid gap-4 md:grid-cols-2">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="bg-card rounded-lg border p-6 animate-pulse">
              <div className="h-6 w-40 bg-muted rounded mb-4" />
              <div className="space-y-3">
                {[...Array(3)].map((_, j) => (
                  <div key={j} className="h-20 bg-muted rounded" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Visão geral do sistema de automações</p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title="Automações Ativas"
          value={`${formatNumber(activeAutomations)} de ${formatNumber(totalAutomations)}`}
          description="automações ativas"
          icon={Zap}
          iconColor="text-blue-600 dark:text-blue-400"
        />

        <StatsCard
          title="Tempo Economizado"
          value={formatTimeSaved(executionStats?.totalTimeSavedMinutes || 0)}
          description="tempo economizado"
          icon={Timer}
          iconColor="text-emerald-600 dark:text-emerald-400"
        />

        <StatsCard
          title="Execuções (Total)"
          value={formatNumber(executionStats?.total || 0)}
          description="Todas execuções"
          icon={PlayCircle}
          iconColor="text-purple-600 dark:text-purple-400"
        />

        <StatsCard
          title="Taxa de Sucesso"
          value={formatPercentage(executionStats?.successRate || 0)}
          description={`${executionStats?.success || 0} sucessos / ${executionStats?.error || 0} erros`}
          icon={CheckCircle}
          iconColor="text-green-600 dark:text-green-400"
          trend={{
            value: 5.2,
            label: 'vs semana passada',
          }}
        />
      </div>

      {/* Charts */}
      <div className="grid gap-4 md:grid-cols-2">
        <ActivityChart data={dashboardData?.activity} />
        <TimeSavedTrendChart data={dashboardData?.timeSavedTrend} />
      </div>

      {/* Alerts */}
      {executionStats?.running > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          {/* Running Executions */}
          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center">
              <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
              <div>
                <p className="font-medium text-blue-900 dark:text-blue-100">
                  {executionStats.running} execução(ões) em andamento
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300">Processando agora...</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lists */}
      <div className="grid gap-4 md:grid-cols-2">
        <AutomationsList />
        <RecentExecutions />
      </div>
    </div>
  );
}
